import { useEffect } from "react";
import { useRoute, useLocation } from "wouter";
import { motion } from "framer-motion";
import { ArrowLeft, Linkedin, Github, Globe, Mail } from "lucide-react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ClientOnly } from "@/components/visual/ClientOnly";
import { Starfield } from "@/components/visual/Starfield";
import { headingFade, springEntrance, stagger } from "@/lib/motion";
import { getFounder } from "@/data/founders";
import { scrollToSection } from "@/lib/section-nav";
import { FounderEmailReveal } from "@/components/sections/FounderEmailReveal";
import NotFound from "@/pages/not-found";

// Simple X (Twitter) glyph — lucide ships no X-brand icon.
function XIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z" />
    </svg>
  );
}

const SOCIAL_LINK =
  "inline-flex items-center justify-center w-11 h-11 rounded-md border border-border bg-card text-muted-foreground transition-colors hover:text-primary hover:border-primary";

export default function FounderProfile() {
  const [, params] = useRoute("/:slug");
  const [, navigate] = useLocation();
  const founder = getFounder(params?.slug);

  // Client-side navigation (wouter <Link>) preserves the window's scroll
  // position, so clicking a leader card from a scrolled-down homepage would
  // open the profile mid-page. Reset to the top whenever the profile changes.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [params?.slug]);

  // Unknown slug → real 404 (cheap; this also renders synchronously during SSG).
  if (!founder) return <NotFound />;

  const base = import.meta.env.BASE_URL;
  const srcSet = `${base}assets/${founder.img}-600.webp 600w, ${base}assets/${founder.img}-1200.webp 1200w`;
  const { socials } = founder;

  return (
    <div className="min-h-screen text-foreground selection:bg-primary/30">
      <ClientOnly>
        <Starfield palette="magellanic" />
      </ClientOnly>

      <Navbar />

      <main className="relative z-10">
        {/* HERO */}
        <section className="container mx-auto px-6 md:px-12 pt-28 md:pt-36 pb-16">
          {/* Client-side nav back to the homepage's leadership section. Home's
              mount effect scrolls to #leadership once it renders. */}
          <a
            href="/#leadership"
            onClick={(e) => {
              e.preventDefault();
              navigate("/#leadership");
              scrollToSection("leadership");
            }}
            className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors mb-10"
          >
            <ArrowLeft className="w-4 h-4" /> Leadership
          </a>

          <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-center">
            <motion.div
              variants={stagger(0.08)}
              initial="hidden"
              animate="visible"
            >
              <motion.p
                variants={headingFade}
                className="text-primary font-mono text-sm tracking-widest uppercase mb-4"
              >
                {founder.role}
              </motion.p>
              <motion.h1
                variants={headingFade}
                className="font-display font-bold text-5xl md:text-7xl tracking-tight leading-[1.02] mb-6"
              >
                {founder.name}
              </motion.h1>
              <motion.p
                variants={headingFade}
                className="text-xl text-muted-foreground max-w-[32ch] leading-relaxed mb-9 [text-wrap:pretty]"
              >
                {founder.lede}
              </motion.p>

              <motion.div variants={headingFade} className="flex flex-wrap items-center gap-3.5">
                <FounderEmailReveal
                  slug={founder.slug}
                  firstName={founder.firstName}
                  trigger={
                    <Button size="lg" className="h-12 px-6 gap-2">
                      <Mail className="w-4 h-4" /> Email
                    </Button>
                  }
                />

                <div className="flex items-center gap-2.5">
                  {socials.linkedin && (
                    <a href={socials.linkedin} target="_blank" rel="noopener" aria-label="LinkedIn" className={SOCIAL_LINK}>
                      <Linkedin className="w-5 h-5" />
                    </a>
                  )}
                  {socials.x && (
                    <a href={socials.x} target="_blank" rel="noopener" aria-label="X (Twitter)" className={SOCIAL_LINK}>
                      <XIcon className="w-[18px] h-[18px]" />
                    </a>
                  )}
                  {socials.github && (
                    <a href={socials.github} target="_blank" rel="noopener" aria-label="GitHub" className={SOCIAL_LINK}>
                      <Github className="w-5 h-5" />
                    </a>
                  )}
                  {socials.website && (
                    <a href={socials.website} target="_blank" rel="noopener" aria-label="Website" className={SOCIAL_LINK}>
                      <Globe className="w-5 h-5" />
                    </a>
                  )}
                </div>
              </motion.div>
            </motion.div>

            <motion.div
              initial="hidden"
              animate="visible"
              variants={springEntrance}
              className="relative"
            >
              <div className="absolute -inset-x-6 -inset-y-8 bg-[radial-gradient(circle_at_60%_40%,hsl(var(--primary)/0.18),transparent_60%)] blur-2xl" />
              <div className="relative rounded-lg overflow-hidden border border-border shadow-2xl shadow-black/60">
                <img
                  src={`${base}assets/${founder.img}-1200.webp`}
                  srcSet={srcSet}
                  sizes="(max-width: 1024px) 90vw, 460px"
                  alt={founder.name}
                  width={founder.imgWidth}
                  height={founder.imgHeight}
                  loading="eager"
                  decoding="async"
                  className="block w-full h-auto aspect-[1200/1480] object-cover object-top grayscale contrast-[1.04] hover:grayscale-0 transition-all duration-500"
                />
                <div className="absolute inset-x-0 bottom-0 px-5 py-4 flex items-center justify-between bg-gradient-to-t from-background/90 to-transparent font-mono text-xs tracking-[0.18em] uppercase">
                  <span className="text-muted-foreground">Pax Magellanic</span>
                  <span className="text-primary">{founder.tag}</span>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* NARRATIVE */}
        <Section eyebrow="Trajectory" title="The long way around.">
          <div className="max-w-[62ch]">
            {founder.narrative.map((para, i) => (
              <p key={i} className="text-lg text-muted-foreground leading-[1.7] mb-5 [text-wrap:pretty]">
                {para}
              </p>
            ))}
          </div>
        </Section>

        {/* TIMELINE */}
        <Section eyebrow="Career" title="Waypoints.">
          <div className="border-l border-border pl-8">
            {founder.timeline.map((stop, i) => (
              <div key={i} className="relative pb-9 last:pb-0">
                <span className="absolute -left-[37px] top-1.5 w-2.5 h-2.5 rounded-full bg-primary ring-4 ring-background shadow-[0_0_14px_hsl(var(--primary)/0.5)]" />
                <p className="font-mono text-xs tracking-[0.18em] uppercase text-primary mb-1.5">
                  {stop.label}
                </p>
                <h3 className="font-display font-semibold text-xl mb-1.5">{stop.title}</h3>
                <p className="text-muted-foreground leading-relaxed max-w-[56ch]">{stop.detail}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* FOCUS */}
        <Section eyebrow="Focus" title="Where the leverage is.">
          <div className="flex flex-wrap gap-3">
            {founder.focus.map((area) => (
              <Badge
                key={area}
                variant="outline"
                className="gap-2 px-4 py-2.5 text-sm font-medium rounded-full"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                {area}
              </Badge>
            ))}
          </div>
        </Section>

        {/* FUN FACTS */}
        <Section eyebrow="Off the clock" title="The human behind the console.">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {founder.funFacts.map((fact, i) => (
              <Card key={i} className="bg-card border-border/60">
                <CardContent className="p-6">
                  <p className="font-mono text-xs tracking-[0.14em] text-primary mb-2.5">{fact.label}</p>
                  <p className="text-muted-foreground leading-relaxed">{fact.value}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </Section>

        {/* CTA */}
        <section className="container mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="relative overflow-hidden rounded-xl border border-border bg-card px-8 md:px-12 py-14 text-center">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_120%,hsl(var(--primary)/0.12),transparent_60%)] pointer-events-none" />
            <div className="relative">
              <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight mb-3">
                Want to talk to {founder.firstName} directly?
              </h2>
              <p className="text-lg text-muted-foreground mb-7">
                Clear the human check and the email address is yours.
              </p>
              <FounderEmailReveal
                slug={founder.slug}
                firstName={founder.firstName}
                trigger={
                  <Button size="lg" className="h-12 px-8 gap-2">
                    <Mail className="w-4 h-4" /> Contact Now
                  </Button>
                }
              />
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

// Shared two-column section scaffold (sticky-feel label + content).
function Section({
  eyebrow,
  title,
  children,
}: {
  eyebrow: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <motion.section
      className="container mx-auto px-6 md:px-12 py-16 md:py-24"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-80px" }}
      variants={headingFade}
    >
      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-10 md:gap-14">
        <div>
          <p className="text-primary font-mono text-sm tracking-widest uppercase mb-3">{eyebrow}</p>
          <h2 className="font-display font-bold text-3xl md:text-[34px] leading-tight tracking-tight">
            {title}
          </h2>
        </div>
        <div>{children}</div>
      </div>
    </motion.section>
  );
}
